<!--
 * @FileDescription: 客户架构树选择器，选中节点后返回末级部门ID列表，用于按客户/部门过滤大客户订单
-->
<template>
  <el-popover v-model="visible" placement="bottom-start" trigger="click" width="260" popper-class="customer-tree-popover">
    <el-input
      v-model="filterText"
      size="mini"
      clearable
      placeholder="Search"
      class="tree-filter-input"
    />
    <div class="tree-wrapper">
      <div v-if="!treeData.length" class="empty-tip">{{ loading ? 'Loading...' : 'No Data' }}</div>
      <el-tree
        v-else
        ref="tree"
        :data="treeData"
        show-checkbox
        node-key="key"
        :props="{ children: 'children', label: 'label' }"
        :filter-node-method="filterNode"
        @check="handleCheck"
      />
    </div>
    <el-input
      slot="reference"
      v-model="displayText"
      size="mini"
      readonly
      clearable
      placeholder="Search Clients"
      style="width:190px;flex-shrink: 0;"
      @clear="handleClear"
    />
  </el-popover>
</template>

<script>
import API from '@/api'

export default {
  data() {
    return {
      visible: false,
      loading: false,
      treeData: [],
      checkedLabels: [],
      filterText: ''
    }
  },
  watch: {
    filterText(val) {
      if (this.$refs.tree) this.$refs.tree.filter(val)
    }
  },
  computed: {
    displayText() {
      if (!this.checkedLabels.length) return ''
      if (this.checkedLabels.length === 1) return this.checkedLabels[0]
      return `${this.checkedLabels[0]} +${this.checkedLabels.length - 1}`
    }
  },
  created() {
    this.fetchTree()
  },
  methods: {
    async fetchTree() {
      this.loading = true
      try {
        let res = await API.getOmsCustomerTree()
        if (res.code !== 10000) {
          this.$message.warning(res.message)
          return
        }
        this.treeData = this.buildTreeData(res.data || [])
      } finally {
        this.loading = false
      }
    },
    /**
     * 把 OMS 返回的"客户 -> 部门"两层结构，转成 el-tree 通用的 children 结构
     * 客户节点没有 departmentId，只有 customerManageId；部门节点相反
     * key 加前缀避免客户ID和部门ID数值撞车
     */
    buildTreeData(customers) {
      return customers.map(customer => ({
        key: `c-${customer.id}`,
        label: customer.name,
        fullPath: customer.name,
        customerManageId: customer.id,
        children: (customer.departments || []).map(dept => this.buildDeptNode(dept, customer.name))
      }))
    },
    buildDeptNode(dept, parentPath) {
      let fullPath = `${parentPath}/${dept.name}`
      return {
        key: `d-${dept.id}`,
        label: dept.name,
        fullPath,
        departmentId: dept.id,
        children: (dept.children || []).map(child => this.buildDeptNode(child, fullPath))
      }
    },
    /**
     * 搜索按全路径匹配而不是节点名：el-tree 过滤时会隐藏"命中节点下未命中的子节点"，
     * 只按 label 匹配的话，搜客户名/中间部门名会只剩一个没有下级的孤节点；
     * 按全路径匹配可让命中节点的整棵子树连同层级一起保留，勾选逻辑不受影响
     */
    filterNode(value, data) {
      if (!value) return true
      return data.fullPath.toLowerCase().indexOf(value.trim().toLowerCase()) !== -1
    },
    /**
     * 选中态变化时，只取末级部门节点（leafOnly=true），
     * 非末级节点/客户节点会被 el-tree 自动展开为其所有末级子节点一并勾选
     */
    handleCheck() {
      let checkedNodes = this.$refs.tree.getCheckedNodes(true)
      let departmentIds = checkedNodes.filter(n => n.departmentId).map(n => n.departmentId)
      this.checkedLabels = checkedNodes.filter(n => n.departmentId).map(n => n.label)
      this.$emit('change', departmentIds)
    },
    handleClear() {
      this.checkedLabels = []
      if (this.$refs.tree) this.$refs.tree.setCheckedKeys([])
      this.$emit('change', [])
    }
  }
}
</script>

<style lang="scss" scoped>
.tree-filter-input {
  margin-bottom: 6px;
}
.tree-wrapper {
  max-height: 320px;
  overflow-y: auto;
  .empty-tip {
    text-align: center;
    color: #909399;
    font-size: 13px;
    padding: 12px 0;
  }
}
</style>
